import React from 'react';
import { LayoutPanelLeft, ArrowDown, ArrowRight, ArrowLeft } from 'lucide-react';

/**
 * Компонент ввода параметров L-образной формы
 */
const LShapeSection = ({ formData, handleInputChange, errors }) => {
  return (
    <div className="mb-6 bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg border border-cyan-100 dark:border-cyan-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
        Размеры L-образной конструкции
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Основная часть L-формы */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Основная часть</h4>
          
          <div className="grid grid-cols-2 gap-3">
            {/* Длина основной части */}
            <div>
              <label htmlFor="lShapePrimaryLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <ArrowRight className="w-4 h-4 mr-1 text-cyan-600" />
                Длина (м)
              </label>
              <input
                type="text"
                id="lShapePrimaryLength"
                name="lShapePrimaryLength"
                value={formData.lShapePrimaryLength}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.lShapeSecondaryLength ? 'input-error' : ''}`}
                placeholder="Длина"
              />
              {errors.lShapeSecondaryLength && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.lShapeSecondaryLength}
                </p>
              )}
            </div>

            {/* Ширина дополнительной части */}
            <div>
              <label htmlFor="lShapeSecondaryWidth" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <ArrowLeft className="w-4 h-4 mr-1 text-cyan-600" />
                Ширина (м)
              </label>
              <input
                type="text"
                id="lShapeSecondaryWidth"
                name="lShapeSecondaryWidth"
                value={formData.lShapeSecondaryWidth}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.lShapeSecondaryWidth ? 'input-error' : ''}`}
                placeholder="Ширина"
              />
              {errors.lShapeSecondaryWidth && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.lShapeSecondaryWidth}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Общая высота для обеих частей */}
        <div className="col-span-1 md:col-span-2">
          <label htmlFor="height" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <ArrowDown className="w-4 h-4 mr-1 text-cyan-600" />
            Высота конструкции (м)
          </label>
          <input
            type="text"
            id="height"
            name="height"
            value={formData.height}
            onChange={handleInputChange}
            className={`form-input w-full sm:w-1/3 ${errors.height ? 'input-error' : ''}`}
            placeholder="Высота"
          />
          {errors.height && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.height}
            </p>
          )}
        </div>
      </div>

      {/* Визуализация L-образной формы */}
      <div className="mt-4 bg-white dark:bg-gray-700 p-4 rounded border border-gray-200 dark:border-gray-600">
        <div className="flex justify-center items-center h-48">
          <div className="relative">
            <svg width="200" height="150" viewBox="0 0 200 150" className="structure-svg">
              <defs>
                <linearGradient id="lShapeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#4299e1" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#2b6cb0" stopOpacity="0.7" />
                </linearGradient>
              </defs>
              {/* Основной L-образный контур */}
              <polygon 
                points={`20,20 
                        ${20 + parseFloat(formData.lShapePrimaryLength) * 10},20 
                        ${20 + parseFloat(formData.lShapePrimaryLength) * 10},${20 + parseFloat(formData.lShapePrimaryWidth) * 10} 
                        ${20 + parseFloat(formData.lShapeSecondaryLength) * 10},${20 + parseFloat(formData.lShapePrimaryWidth) * 10}
                        ${20 + parseFloat(formData.lShapeSecondaryLength) * 10},${20 + parseFloat(formData.lShapePrimaryWidth) * 10 + parseFloat(formData.lShapeSecondaryWidth) * 10}
                        20,${20 + parseFloat(formData.lShapePrimaryWidth) * 10 + parseFloat(formData.lShapeSecondaryWidth) * 10}
                        `}
                fill="url(#lShapeGradient)" 
                stroke="#2c5282" 
                strokeWidth="2" 
              />
              
              {/* Размеры основной части */}
              <text 
                x={20 + (parseFloat(formData.lShapePrimaryLength) * 10) / 2} 
                y="15" 
                textAnchor="middle" 
                fontSize="10" 
                fill="#2c5282"
              >
                {formData.lShapePrimaryLength} м
              </text>
              
              <text 
                x="15" 
                y={20 + (parseFloat(formData.lShapePrimaryWidth) * 10) / 2} 
                textAnchor="middle" 
                fontSize="10" 
                fill="#2c5282"
                transform={`rotate(-90, 15, ${20 + (parseFloat(formData.lShapePrimaryWidth) * 10) / 2})`}
              >
                {formData.lShapePrimaryWidth} м
              </text>
              
              {/* Размеры дополнительной части */}
              <text 
                x={20 + (parseFloat(formData.lShapeSecondaryLength) * 10) / 2} 
                y={20 + parseFloat(formData.lShapePrimaryWidth) * 10 + parseFloat(formData.lShapeSecondaryWidth) * 10 + 15} 
                textAnchor="middle" 
                fontSize="10" 
                fill="#2c5282"
              >
                {formData.lShapeSecondaryLength} м
              </text>
              
              <text 
                x={20 + parseFloat(formData.lShapeSecondaryLength) * 10 + 15} 
                y={20 + parseFloat(formData.lShapePrimaryWidth) * 10 + (parseFloat(formData.lShapeSecondaryWidth) * 10) / 2} 
                textAnchor="middle" 
                fontSize="10" 
                fill="#2c5282"
                transform={`rotate(90, ${20 + parseFloat(formData.lShapeSecondaryLength) * 10 + 15}, ${20 + parseFloat(formData.lShapePrimaryWidth) * 10 + (parseFloat(formData.lShapeSecondaryWidth) * 10) / 2})`}
              >
                {formData.lShapeSecondaryWidth} м
              </text>
              
              {/* Высота */}
              <text 
                x={20 + parseFloat(formData.lShapePrimaryLength) * 10 / 2} 
                y={20 + parseFloat(formData.lShapePrimaryWidth) * 10 / 2} 
                textAnchor="middle" 
                fontSize="10" 
                fill="#ffffff"
                fontWeight="bold"
              >
                h: {formData.height} м
              </text>
            </svg>
          </div>
        </div>
      </div>

      <div className="mt-4 text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 p-3 rounded border border-gray-200 dark:border-gray-600">
        <p>L-образная форма состоит из двух прямоугольных секций. Объем рассчитывается как сумма объемов этих секций с общей высотой.</p>
        <p className="mt-1">V = (длина1 × ширина1 × высота) + (длина2 × ширина2 × высота)</p>
      </div>
    </div>
  );
};

export default LShapeSection;